import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { entidadesApi } from '../../services/api';
import {
  Briefcase, CheckSquare, Mail, PenLine, FileText, Loader2, ChevronRight,
} from 'lucide-react';

const TIPO_CONFIG = {
  visita:        { icon: Briefcase,   color: 'bg-sky-100 text-sky-600',      label: 'Visita' },
  tarea:         { icon: CheckSquare, color: 'bg-emerald-100 text-emerald-600', label: 'Tarea' },
  correo:        { icon: Mail,        color: 'bg-violet-100 text-violet-600', label: 'Correo' },
  cambio_estado: { icon: PenLine,     color: 'bg-amber-100 text-amber-600',   label: 'Cambio' },
  documento:     { icon: FileText,    color: 'bg-slate-100 text-slate-600',   label: 'Documento' },
};

const LINK_BASE = {
  visita:    (id) => `/visitas/${id}`,
  tarea:     (id) => `/tareas?id=${id}`,
  correo:    () => null,
  cambio_estado: () => null,
  documento: () => null,
};

function fmtFechaHora(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export function HistorialTab({ entidadId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    entidadesApi.historial(entidadId, 50)
      .then(r => setItems(r.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [entidadId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-ink-400 py-8 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando historial…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-ink-400">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>Sin historial disponible para esta empresa.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1 py-2">
      {items.map((item, i) => {
        const cfg = TIPO_CONFIG[item.tipo] || TIPO_CONFIG.documento;
        const Icon = cfg.icon;
        const href = LINK_BASE[item.tipo]?.(item.link_id);
        const isClickable = !!href;

        const inner = (
          <div className={`flex items-start gap-3 px-3 py-3 rounded-lg ${isClickable ? 'hover:bg-ink-50 cursor-pointer' : ''} transition-colors`}>
            {/* Icono */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${cfg.color}`}>
              <Icon className="w-4 h-4" />
            </div>
            {/* Contenido */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-ink-800 truncate">{item.titulo}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
              </div>
              {item.descripcion && (
                <p className="text-xs text-ink-500 mt-0.5 line-clamp-2">{item.descripcion}</p>
              )}
              <div className="flex items-center gap-2 mt-1 text-[11px] text-ink-400">
                <span>{fmtFechaHora(item.fecha)}</span>
                {item.usuario && <><span>·</span><span>{item.usuario}</span></>}
              </div>
            </div>
            {isClickable && <ChevronRight className="w-4 h-4 text-ink-300 shrink-0 mt-2" />}
          </div>
        );

        return isClickable
          ? <button key={i} className="w-full text-left" onClick={() => navigate(href)}>{inner}</button>
          : <div key={i}>{inner}</div>;
      })}
    </div>
  );
}
